import { useEffect, useState, type ReactNode } from 'react';
import { FilterBar } from '../components/Filters';
import {
  downloadCsv,
  localTime,
  num,
  pct,
  useApi,
  useFilters,
  useQuery,
  type FbAttention,
  type FbDriver,
  type FbRegion,
  type FeedbackResponse,
  type FeedbackStopRow,
  type FeedbackStopsResponse,
  type FiltersResponse,
  type RatingBucket,
} from '../lib/reports';

const STAR_GOLD = '#f5a623';

/** Five stars, filled up to `value` (rounded). */
function Stars({ value, tone = 'gold' }: { value: number | null; tone?: 'gold' | 'light' }) {
  const filled = Math.round(value ?? 0);
  const empty = tone === 'light' ? 'rgba(255,255,255,0.35)' : '#d9dce2';
  const on = tone === 'light' ? '#fff' : STAR_GOLD;
  return (
    <span className="stars" aria-label={`${value ?? 0} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ color: i <= filled ? on : empty }}>
          ★
        </span>
      ))}
    </span>
  );
}

function barColor(stars: number): string {
  if (stars >= 4) return '#1f6fd6';
  if (stars === 3) return '#9aa1ac';
  return '#e4002b';
}

export function CustomerFeedback() {
  const [filters, patch] = useFilters();
  const qs = useQuery(filters);
  const [stars, setStars] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, error, loading } = useApi<FeedbackResponse>(`/api/reports/feedback?${qs}`);
  const { data: options } = useApi<FiltersResponse>('/api/reports/difot/filters');
  const stopsQs = `${qs}${stars ? `&stars=${stars}` : ''}${debounced ? `&search=${encodeURIComponent(debounced)}` : ''}&limit=200`;
  const { data: stops } = useApi<FeedbackStopsResponse>(`/api/reports/feedback/stops?${stopsQs}`);

  const s = data?.summary;

  function exportStops() {
    const rows = (stops?.rows ?? []).map((r: FeedbackStopRow) => [
      r.completed_time,
      r.rating,
      r.feedback,
      r.driver_name,
      r.region,
      r.customer_name,
      r.source_reference,
    ]);
    downloadCsv(
      `amart-feedback-${filters.from}_${filters.to}.csv`,
      ['completed', 'rating', 'feedback', 'driver', 'region', 'store', 'source_ref'],
      rows,
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reporting / Customer feedback</p>
          <h1>Customer feedback</h1>
          <p className="muted">
            {s ? `${num(s.ratings)} ratings · ` : ''}
            {filters.from} → {filters.to} · customer ratings &amp; comments per delivery
          </p>
        </div>
      </header>

      <FilterBar
        filters={filters}
        options={options ?? null}
        onChange={patch}
        onExport={exportStops}
        exportLabel="Export ratings"
      />

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {loading && !data && <p className="muted">Loading…</p>}

      {s && data && (
        <>
          {/* KPI cards */}
          <div className="fb-kpis">
            <div className="fb-kpi primary">
              <span className="fb-eyebrow">Average rating</span>
              <div className="fb-avg">
                <strong>{s.avg_rating != null ? s.avg_rating.toFixed(2) : '—'}</strong>
                <span className="fb-avg-max">/ 5</span>
              </div>
              <Stars value={s.avg_rating} tone="light" />
              <span className="fb-kpi-foot">Across {num(s.ratings)} customer ratings</span>
            </div>

            <div className="fb-kpi">
              <span className="fb-eyebrow">Response rate</span>
              <div className="fb-stat">
                <strong>{num(s.ratings)}</strong>
                <span className="fb-stat-sub">/ {num(s.completed)}</span>
              </div>
              <span className="fb-kpi-mid">{pct(s.response_rate_pct, 2)} of completed stops were rated</span>
              <div className="fb-progress">
                <span style={{ width: `${Math.min(100, s.response_rate_pct ?? 0)}%` }} />
              </div>
              <span className="fb-kpi-foot">Rated vs completed stops in the window</span>
            </div>

            <div className="fb-kpi">
              <span className="fb-eyebrow">Low ratings (≤ 2★)</span>
              <div className="fb-stat">
                <strong className="fb-danger">{num(s.low)}</strong>
                <span className="fb-stat-sub">({pct(s.low_pct, 2)})</span>
              </div>
              <span className="fb-kpi-foot">Customers who rated 2 stars or below</span>
            </div>
          </div>

          {/* Distribution */}
          <div className="card">
            <div className="card-head">
              <div>
                <p className="eyebrow">Distribution</p>
                <h2>Ratings breakdown</h2>
                <p className="muted small">Click a row to filter the drill-down to that rating</p>
              </div>
            </div>
            <div className="fb-dist">
              {data.distribution.map((b: RatingBucket) => (
                <button
                  key={b.stars}
                  type="button"
                  className={`fb-dist-row${stars === b.stars ? ' active' : ''}`}
                  onClick={() => setStars(stars === b.stars ? null : b.stars)}
                >
                  <span className="fb-dist-stars">
                    <Stars value={b.stars} />
                  </span>
                  <span className="fb-dist-bar">
                    <span style={{ width: `${b.pct ?? 0}%`, background: barColor(b.stars) }} />
                  </span>
                  <span className="fb-dist-pct">{pct(b.pct, 1)}</span>
                  <span className="fb-dist-n">{num(b.n)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Leaderboards */}
          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <div>
                  <p className="eyebrow">People</p>
                  <h2>Driver leaderboard</h2>
                  <p className="muted small">Drivers with the highest average rating in this window</p>
                </div>
              </div>
              <LeaderList
                rows={data.driver_leaderboard}
                empty="No ratings in this window."
                render={(d: FbDriver, i) => (
                  <li key={d.driver_name}>
                    <span className="lead-rank">{i + 1}</span>
                    <span className="lead-name">{d.driver_name}</span>
                    <Stars value={d.avg_rating} />
                    <span className="lead-val">{(d.avg_rating ?? 0).toFixed(2)}</span>
                    <span className="lead-n">{num(d.ratings)} ratings</span>
                  </li>
                )}
              />
            </div>

            <div className="card">
              <div className="card-head">
                <div>
                  <p className="eyebrow">Places</p>
                  <h2>Team region leaderboard</h2>
                  <p className="muted small">Regions ranked by their customers&rsquo; average rating</p>
                </div>
              </div>
              <LeaderList
                rows={data.region_leaderboard}
                empty="No ratings in this window."
                render={(r: FbRegion, i) => (
                  <li key={r.region}>
                    <span className="lead-rank">{i + 1}</span>
                    <span className="lead-name">{r.region}</span>
                    <Stars value={r.avg_rating} />
                    <span className="lead-val">{(r.avg_rating ?? 0).toFixed(2)}</span>
                    <span className="lead-n">{num(r.ratings)} ratings</span>
                  </li>
                )}
              />
            </div>
          </div>

          {/* Drivers needing attention */}
          <div className="card">
            <div className="card-head">
              <div>
                <h2>Drivers needing attention</h2>
                <p className="muted small">
                  Drivers with the most low ratings (≤ 2★) out of their total ratings in this window — candidates
                  for additional training
                </p>
              </div>
            </div>
            <AttentionList rows={data.driver_attention} />
          </div>

          {/* Drill-down */}
          <div className="card">
            <div className="card-head">
              <div>
                <p className="eyebrow">Drill down</p>
                <h2>
                  Rated stops {stars && <span className="chip">{stars}★</span>}
                  {stars && (
                    <button type="button" className="link-btn" onClick={() => setStars(null)}>
                      clear
                    </button>
                  )}
                </h2>
                <p className="muted small">
                  Showing {num(stops?.rows.length ?? 0)} of {num(stops?.total ?? 0)} rated stops
                </p>
              </div>
              <input
                className="fb-search"
                type="search"
                placeholder="Search reference, driver, region, store…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <StopsTable rows={stops?.rows ?? []} />
          </div>
        </>
      )}
    </section>
  );
}

function LeaderList<T>({ rows, render, empty }: { rows: T[]; render: (row: T, i: number) => ReactNode; empty: string }) {
  if (rows.length === 0) return <p className="muted">{empty}</p>;
  return <ol className="leaderboard">{rows.map((r, i) => render(r, i))}</ol>;
}

function AttentionList({ rows }: { rows: FbAttention[] }) {
  if (rows.length === 0) return <p className="muted">No low ratings in this window — nice work.</p>;
  const maxLow = Math.max(1, ...rows.map((r) => r.low));
  return (
    <ol className="attention">
      {rows.map((r, i) => (
        <li key={r.driver_name}>
          <span className="lead-rank">{i + 1}</span>
          <span className="att-name">{r.driver_name}</span>
          <span className="att-bar">
            <span style={{ width: `${(r.low / maxLow) * 100}%` }} />
          </span>
          <span className="att-count">
            <b>{num(r.low)}</b> / {num(r.total)}
          </span>
          <span className="att-pct">{pct(r.pct_low, 1)}</span>
        </li>
      ))}
    </ol>
  );
}

function StopsTable({ rows }: { rows: FeedbackStopRow[] }) {
  if (rows.length === 0) return <p className="muted">No rated stops match the current filters.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Completed (local)</th>
            <th>Rating</th>
            <th>Feedback</th>
            <th>Driver</th>
            <th>Region</th>
            <th>Store</th>
            <th>Source ref</th>
            <th>Stop</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.stop_id}>
              <td className="nowrap">{localTime(r.completed_time, r.tz)}</td>
              <td className="nowrap">
                <Stars value={r.rating} /> <b>{r.rating}</b>
              </td>
              <td className="fb-feedback">{r.feedback ?? <span className="muted">No comment</span>}</td>
              <td>{r.driver_name ?? '—'}</td>
              <td>{r.region ?? '—'}</td>
              <td>{r.customer_name ?? '—'}</td>
              <td className="nowrap">{r.source_reference ?? '—'}</td>
              <td>
                {r.tracking_link ? (
                  <a href={r.tracking_link} target="_blank" rel="noreferrer">
                    Open ↗
                  </a>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
